/**
 * Theme / density / locale helpers for the SPA.
 *
 * Settings are persisted to `localStorage` and applied to <html> via the
 * `data-theme` / `data-density` attributes; locale flips i18next + html lang.
 *
 * Themes:
 *   - 'white' / 'black' — the only palettes. Monochrome, accent-free.
 *   - 'system'          — follows the OS, resolving to white/black.
 *
 * The Klaret light/dark pair (warm paper + claret accent) was RETIRED. A stored
 * 'light'/'dark' no longer passes the allow-list in getTheme(), so it falls back
 * to the default — existing users migrate silently rather than being stranded on
 * a theme whose tokens no longer exist.
 *
 * NOTE: this file is intentionally duplicated in apps/admin and
 * apps/client-portal (kept identical). It lives app-local rather than in
 * shared-utils because shared-utils is Node-typed (no DOM lib) and this code
 * uses browser globals (localStorage/window/document). Mirror any edit.
 *
 * `applyAtBoot()` should run before React renders so the first paint already
 * carries the user's preferences (no flash of the wrong theme).
 */
import { i18n } from '@teivor/shared-utils/i18n';

export type Theme = 'white' | 'black' | 'system';
export type Density = 'compact' | 'normal' | 'comfortable';
export type Locale = 'cs' | 'en';

/** The concrete `data-theme` values written to <html> (never 'system'). */
export type ResolvedTheme = 'white' | 'black';

const KEYS = {
  theme: 'teivor.theme',
  density: 'teivor.density',
  locale: 'teivor.locale',
} as const;

const DEFAULTS: { theme: Theme; density: Density; locale: Locale } = {
  theme: 'system',
  density: 'normal',
  locale: 'cs',
};

function readLS<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    if (v && (allowed as readonly string[]).includes(v)) return v as T;
  } catch {
    /* ignore */
  }
  return fallback;
}

function writeLS(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function resolveTheme(t: Theme): ResolvedTheme {
  if (t === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'black' : 'white';
    }
    return 'white';
  }
  // 'white' | 'black' are applied verbatim.
  return t;
}

let mediaCleanup: (() => void) | null = null;

export function applyTheme(t: Theme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', resolveTheme(t));
  // legacy `theme` localStorage key kept in sync for any other consumers
  writeLS('theme', resolveTheme(t));
  writeLS(KEYS.theme, t);

  // Re-apply on system preference change when in 'system' mode.
  if (mediaCleanup) {
    mediaCleanup();
    mediaCleanup = null;
  }
  if (t === 'system' && typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      root.setAttribute('data-theme', resolveTheme('system'));
    };
    mq.addEventListener?.('change', handler);
    mediaCleanup = () => mq.removeEventListener?.('change', handler);
  }
}

export function applyDensity(d: Density): void {
  document.documentElement.setAttribute('data-density', d);
  writeLS(KEYS.density, d);
}

export function applyLocale(l: Locale): void {
  document.documentElement.setAttribute('lang', l);
  writeLS(KEYS.locale, l);
  void i18n.changeLanguage(l);
}

export function getTheme(): Theme {
  return readLS<Theme>(
    KEYS.theme,
    ['white', 'black', 'system'] as const,
    DEFAULTS.theme,
  );
}
export function getDensity(): Density {
  return readLS<Density>(
    KEYS.density,
    ['compact', 'normal', 'comfortable'] as const,
    DEFAULTS.density,
  );
}
export function getLocale(): Locale {
  return readLS<Locale>(KEYS.locale, ['cs', 'en'] as const, DEFAULTS.locale);
}

export function applyAtBoot(): void {
  applyTheme(getTheme());
  applyDensity(getDensity());
  applyLocale(getLocale());
}
